// src/pages/Login.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API_BASE_URL from '../utils/apiBase';

// Mobile apps can register a custom URL scheme and receive the SSO token
// via a deep link. Each app that wants to consume SI SSO must be listed here.
const ALLOWED_CUSTOM_SCHEMES = new Set<string>([
  'agentsnowbird',
  'snowbird', // unified Snowbird Industries mobile app (snowbird://auth/callback)
]);

const isAllowedRedirect = (url: string): boolean => {
  if (url.startsWith('/')) {
    return true;
  }

  // Custom scheme (e.g. agentsnowbird://auth/callback) — validated by
  // scheme name alone, since new URL() cannot reliably parse their paths
  // across browsers and we don't need to validate the host.
  const schemeMatch = url.match(/^([a-z][a-z0-9+.-]*):/i);
  if (schemeMatch) {
    const scheme = schemeMatch[1].toLowerCase();
    if (ALLOWED_CUSTOM_SCHEMES.has(scheme)) {
      return true;
    }
  }

  try {
    const parsed = new URL(url);
    const { hostname } = parsed;
    return (
      hostname === 'snowbirdindustries.us' ||
      hostname.endsWith('.snowbirdindustries.us') ||
      hostname === 'agentsnowbird.com' ||
      hostname.endsWith('.agentsnowbird.com') ||
      hostname === 'localhost' ||
      hostname === '127.0.0.1'
    );
  } catch (error) {
    return false;
  }
};

const withToken = (url: string, token?: string | null): string => {
  if (!token) {
    return url;
  }
  // Try the URL parser first — works for http(s) redirects.
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('token', token);
    return parsed.toString();
  } catch {
    // Custom schemes (agentsnowbird://) fail URL parsing in some browsers,
    // so fall back to manual query-string append. We strip any existing
    // ?token= first so a replay doesn't get two copies.
    const cleaned = url.replace(/([?&])token=[^&]*(&|$)/, (_m, p1, p2) => (p2 ? p1 : ''));
    const separator = cleaned.includes('?') ? '&' : '?';
    return `${cleaned}${separator}token=${encodeURIComponent(token)}`;
  }
};

/**
 * Exchange a possibly-expired token for a fresh one via the SI backend
 * /auth/refresh endpoint. Used when we're about to redirect to an external
 * app — the user's localStorage token may be days old and expired, but the
 * refresh endpoint re-issues as long as the signature is valid and the
 * user still exists. Returns null on failure so the caller can fall through
 * to re-authentication.
 */
const refreshSIToken = async (staleToken: string | null): Promise<string | null> => {
  if (!staleToken) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${staleToken}`,
      },
      body: JSON.stringify({ token: staleToken }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (data?.token) {
      localStorage.setItem('authToken', data.token);
      return data.token;
    }
    return null;
  } catch {
    return null;
  }
};

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const redirectTarget = redirectParam && isAllowedRedirect(redirectParam) ? redirectParam : null;

  const from = (location.state as any)?.from?.pathname || '/';

  // If already logged in, skip the login form entirely
  useEffect(() => {
    if (!isAuthenticated) return;
    if (redirectTarget && !redirectTarget.startsWith('/')) {
      // External app redirect — always refresh the token first. The
      // localStorage copy may be days old and already expired, which
      // would send an unusable JWT to the downstream app.
      (async () => {
        const fresh = (await refreshSIToken(token)) || token;
        window.location.assign(withToken(redirectTarget, fresh));
      })();
    } else {
      navigate(redirectTarget || from || '/', { replace: true });
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const success = await login(email, password);
      if (success) {
        if (redirectTarget) {
          const token = localStorage.getItem('authToken');
          const targetUrl = redirectTarget.startsWith('/')
            ? redirectTarget
            : withToken(redirectTarget, token);
          if (targetUrl.startsWith('/')) {
            navigate(targetUrl, { replace: true });
          } else {
            window.location.assign(targetUrl);
          }
          return;
        }
        navigate(from, { replace: true });
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="section container">
      <div style={{
        maxWidth: '450px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>Sign In</h1>
        <p className="text-secondary" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Access your Snowbird Industries account for apps, games, and resources
        </p>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fee2e2',
              border: '1px solid #ef4444',
              borderRadius: '6px',
              color: '#dc2626',
            }}>
              {error}
            </div>
          )}

          <div>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}
            >
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="••••••••"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary"
            style={{
              width: '100%',
              padding: '0.75rem',
              opacity: isLoading ? 0.6 : 1,
              cursor: isLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center' }}>
          <a href={redirectTarget ? `/forgot-password?redirect=${encodeURIComponent(redirectTarget)}` : '/forgot-password'}>
            Forgot your password?
          </a>
        </p>
        <p style={{ marginTop: '0.75rem', textAlign: 'center' }}>
          <a href={redirectTarget ? `/signup?redirect=${encodeURIComponent(redirectTarget)}` : '/signup'}>
            Create an account
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
